/**
 * SignDocument — the driver's signing ceremony, at `/sign/:documentId`.
 *
 * Up to seven steps, in the order a person can actually follow:
 *
 *   disclose  the two prices, stated OUTSIDE the agreement and BEFORE it opens (hire
 *             purchase only)
 *   intro     the four things this agreement says, in plain English, before any legal text
 *   read      the whole Schedule, paginated, gated on reaching the end AND on time spent
 *   booklet   the standing Terms the Schedule incorporates, shown and accepted (hire
 *             purchase and the two advances only)
 *   terms     the same four things again, one tick at a time
 *   sign      who you are signing as, a typed name, a drawn signature, one attestation
 *   done      what happens next, and the copy once it exists
 *
 * ── THE STEP LIST IS PER DOCUMENT ─────────────────────────────────────────────
 * `disclose` and `booklet` exist because two specific instruments say they happened. A
 * deposit agreement and a witness agreement say neither, so their ceremonies are the five
 * steps they always were, and the progress rail is `steps.indexOf(step)` rather than a
 * literal — a hard-coded index against a list that changes length is an off-by-one waiting
 * for the first advance.
 *
 * THE ID `terms` IS THE CONFIRMATION CHECKLIST AND ALWAYS HAS BEEN. The Terms booklet is
 * `booklet`. Renaming `terms` would orphan every live `CeremonyDraft.step` in a signer's
 * localStorage, which is a resumed ceremony dropped back to the beginning for a rename.
 *
 * ── THE HASH IS THE SERVER'S ──────────────────────────────────────────────────
 * `agreement_sign` re-renders the document in SQL and refuses a signature whose submitted
 * `content_sha256` does not match. The value submitted here is therefore `agreement_render`'s
 * own, verbatim. This page digests the markdown independently only as a CROSS-CHECK, and a
 * disagreement blocks signing rather than being papered over: it means the bytes on screen
 * are not the bytes the server hashed, which is the one situation where signing is wrong.
 *
 * ── THE STORAGE SCOPE IS THE SERVER'S TOO ─────────────────────────────────────
 * The signature PNG is uploaded before `agreement_sign` is called, so the path has to be
 * right the FIRST time — a refused path leaves an orphan object in a bucket a driver cannot
 * delete from. Which folder it is, is `agreement_path_scope`'s answer, arriving on
 * `agreement_render` as `path_scope`, and it is not the contract on every document: a
 * DEPOSIT agreement is filed under its deposit arc even when the deposit already has a
 * contract, and the driver on such a deposit signs it right here, in session.
 *
 * ── IDEMPOTENCE ───────────────────────────────────────────────────────────────
 * `agreement_sign` answers `{signed: false, reason: 'already_signed'}` rather than raising
 * when the party has already signed, so a double tap, a retried request or a resumed draft
 * lands on the done step instead of on an error.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ArrowRight, Eye, FileText, Loader2, RotateCw } from 'lucide-react';
import { toast } from 'sonner';

import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { captureError } from '@/lib/logger';
import { agreementMarkdownToPages } from '@/lib/agreement-markdown';
import {
    agreementKeyTerms,
    scheduleDepositCredited,
    scheduleWeeklyInstalment,
} from '@/lib/agreement-key-terms';
import {
    parseTermsCitation,
    termsBookletUrl,
    termsCitationLabel,
} from '@/lib/agreement-terms-citation';
import { PARTY_NOUNS, ceremonySteps, type Step } from '@/lib/ceremony-steps';
import {
    CONSENT_TEXT_VERSION,
    SIGNATURE_BUCKET,
    SIGNING_DISABLED,
    clearDraft,
    dataUrlToBlob,
    isMyParty,
    loadDraft,
    randomUuid,
    saveDraft,
    sha256Hex,
    signatureObjectPath,
    readerGateOpen,
    signingError,
} from '@/lib/esign';
import { signDoneTrail } from '@/lib/sign-done-trail';
import {
    useAgreementDocument,
    useAgreementDocuments,
    useAgreementRender,
    useContractInsights,
} from '@/hooks/useAgreements';
import { useAgreementTerms } from '@/hooks/useAgreementTerms';
import { useContractDisclosure } from '@/hooks/useContractDisclosure';
import CeremonyShell, { CeremonyNote } from '@/components/agreements/CeremonyShell';
import {
    ConfirmRow,
    DoneMark,
    Eyebrow,
    FrozenNote,
    KeyTermRow,
    SigningAs,
    StepHeading,
    TrailRow,
} from '@/components/agreements/CeremonyPieces';
import DisclosurePanel from '@/components/agreements/DisclosurePanel';
import DocumentReader from '@/components/agreements/DocumentReader';
import TermsBooklet from '@/components/agreements/TermsBooklet';
import SignaturePad, { type SignaturePadHandle } from '@/components/agreements/SignaturePad';
import SignatoryChips from '@/components/agreements/SignatoryChips';

/** Seconds the reader step insists on before "Continue" opens. */
const READ_SECONDS_FLOOR = 45;

const ATTESTATION =
    'I intend this drawn signature to be my signature on this agreement, and I understand ' +
    'it has the same legal effect as signing on paper.';

function ceremonyBody(children: ReactNode) {
    return <div className="space-y-4">{children}</div>;
}

export default function SignDocument() {
    const { documentId } = useParams<{ documentId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const parsedId = Number(documentId);
    const id = Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;

    const documentQuery = useAgreementDocument(id);
    const renderQuery = useAgreementRender(id);
    const agreementDoc = documentQuery.data ?? null;
    const rendered = renderQuery.data ?? null;
    const insightsQuery = useContractInsights(agreementDoc?.contract_id ?? null);
    // THE OTHER DOCUMENTS ON THIS CONTRACT, for the done step's trail alone: the witness now
    // signs the WITNESS agreement, so their state is not on this document's roster. Same
    // query key the Documents page fills and the sign mutation invalidates, so this is
    // normally a cache read rather than a wire call — and an empty answer degrades the
    // witness row to its static form instead of dropping it (see `signDoneTrail`).
    //
    // ONLY THE HIRE PURCHASE AGREEMENT READS IT. `signDoneTrail` draws no witness row on a
    // loan, an insurance loan or a deposit, so on those the answer is fetched and discarded —
    // and the hook cannot be skipped with an `if`, which is what the `enabled` option is for.
    const contractDocumentsQuery = useAgreementDocuments(agreementDoc?.contract_id ?? null, {
        enabled: agreementDoc?.doc_type === 'HIRE_PURCHASE',
    });

    // THE BOOKLET IS NAMED BY THE BYTES BEING SIGNED, not by a foreign key — there is none.
    // Clause 3.1 of the frozen body carries the form code, version and the first sixteen
    // characters of the digest, and `agreement-terms-citation.ts` reads them back out.
    const termsCitation = useMemo(
        () => parseTermsCitation(rendered?.rendered_md),
        [rendered?.rendered_md],
    );
    const termsQuery = useAgreementTerms(termsCitation);
    // Held apart from every other read on this page on purpose: `contracts.cash_price` is
    // staging-only until fleet-backend `20260816010000` promotes, and a 42703 has to cost one
    // panel rather than the whole ceremony. See the hook's own note.
    const disclosureQuery = useContractDisclosure(agreementDoc?.contract_id ?? null, {
        enabled: agreementDoc?.doc_type === 'HIRE_PURCHASE',
    });

    const [step, setStep] = useState<Step>('intro');
    const [page, setPage] = useState(1);
    const [pagesSeen, setPagesSeen] = useState(1);
    const [readSeconds, setReadSeconds] = useState(0);
    const [terms, setTerms] = useState<boolean[]>([]);
    const [typedName, setTypedName] = useState('');
    const [attested, setAttested] = useState(false);
    const [hasInk, setHasInk] = useState(false);
    const [signatureProof, setSignatureProof] = useState<string | null>(null);
    const [hashOk, setHashOk] = useState<boolean | null>(null);

    // ── The booklet step's own progress, deliberately NOT in the draft ────────
    // `CeremonyDraft` lives in esign.ts and carries the Schedule reader's page, seconds and
    // ticks. It carries none of these, so a reload sends the signer back through the booklet
    // — which is the only honest behaviour for a gate whose claim is "this was shown", since
    // a restored gate would vouch for a display this session never made.
    const [bookletSeconds, setBookletSeconds] = useState(0);
    const [bookletEnded, setBookletEnded] = useState(false);
    const [bookletAccepted, setBookletAccepted] = useState(false);

    const padRef = useRef<SignaturePadHandle | null>(null);
    const draftScope = id ? String(id) : '';
    const draftRestored = useRef(false);
    /** The terms row already reported as accepted, so a re-tick does not re-report. */
    const termsRecorded = useRef<number | null>(null);

    // ── Derived document facts ────────────────────────────────────────────────
    const myParty = useMemo(
        () => (rendered?.parties ?? []).find((party) => isMyParty(party.party)) ?? null,
        [rendered],
    );
    const alreadySigned = myParty?.status === 'SIGNED';
    const documentClosed =
        rendered?.status === 'VOIDED' || rendered?.status === 'SUPERSEDED';

    // `values` is `agreement_render`'s own merge map and the ONLY source of a resolved
    // signature block; the roster only decides which blocks get a fill-in. It is admin-only
    // in SQL, so on a driver's session it arrives null and every block reads as awaiting.
    const pages = useMemo(
        () =>
            agreementMarkdownToPages(rendered?.rendered_md, {
                parties: rendered?.parties,
                values: rendered?.values,
            }),
        [rendered],
    );

    // THE TITLE COMES FROM THE RENDER AND NOT FROM THE ROW: `agreement_documents.title` was
    // dropped as a column, and the render reads it off the PINNED template this document was
    // raised against. One `doc_type` now carries two insurance instruments, so it is the title
    // that tells the checklist which of them the driver is about to sign — see
    // `PREMIUM_PAYMENT_RECORD_TITLE`. Until the render lands it is undefined, and the advance
    // checklist stands, which is the list that has always been true here.
    const keyTerms = useMemo(
        () =>
            agreementDoc
                ? agreementKeyTerms(
                      {
                          doc_type: agreementDoc.doc_type,
                          reference_no: agreementDoc.reference_no,
                          title: rendered?.title,
                          loan_agreement_id: agreementDoc.loan_agreement_id,
                          insurance_loan_agreement_id: agreementDoc.insurance_loan_agreement_id,
                          contract_deposit_agreement_id: agreementDoc.contract_deposit_agreement_id,
                      },
                      insightsQuery.data,
                  )
                : [],
        [agreementDoc, rendered?.title, insightsQuery.data],
    );

    const driverName = useMemo(() => {
        const driver = insightsQuery.data?.driver;
        if (!driver) return 'You';
        return (
            [driver.first_name, driver.middle_name, driver.last_name].filter(Boolean).join(' ') ||
            'You'
        );
    }, [insightsQuery.data]);

    // ── The booklet, and the step it needs ────────────────────────────────────
    const steps = useMemo(
        () => ceremonySteps(agreementDoc?.doc_type, termsCitation !== null),
        [agreementDoc?.doc_type, termsCitation],
    );

    const termsRow = termsQuery.data ?? null;

    /**
     * The booklet's own pages. ONE page in practice — the `:::pagebreak:::` fences were taken
     * out of all three Terms bodies on 2026-08-13 — but joined rather than paginated either
     * way, because the booklet is a continuous read gated on reaching its end and a pager
     * would be a second, contradictory idea of where the end is.
     */
    const bookletPages = useMemo(
        () => (termsRow ? agreementMarkdownToPages(termsRow.body_md) : null),
        [termsRow],
    );

    /** The row's own title, else the cited sentence's, else the bare form code. */
    const bookletName =
        termsRow?.title ??
        termsCitation?.title ??
        (termsCitation ? `Terms, form ${termsCitation.formCode}` : '');

    /**
     * Clause 3.3, in the first person and in this instrument's own nouns.
     *
     * SHOWN ONLY WHEN THE BOOKLET WAS ACTUALLY SHOWN. A tick reading "I have been shown" on a
     * screen that could not display the Terms would collect an attestation to something that
     * did not happen — the one failure this whole step exists to end. When the booklet does
     * not resolve the signer reads the citation, gets no tick, and carries on; clause 3.3 is
     * inside the frozen bytes their signature seals either way, and that record is not this
     * app's to weaken or to counterfeit.
     */
    const bookletAcceptance = termsCitation
        ? `I have been shown the ${bookletName}, version ${termsCitation.version} ` +
          `(form ${termsCitation.formCode}), and I accept them as part of this agreement.`
        : '';

    /**
     * WHAT WAS SHOWN, AS ONE VALUE — the citation printed in the bytes being signed, plus the
     * row that citation resolved to.
     *
     * Both halves matter. Two different Schedules can cite the same booklet, and one citation
     * can resolve to no row now and to a row a moment later (`useAgreementTerms` answers null
     * for a policy that has not widened yet), so neither half alone identifies the display
     * this session actually made.
     */
    const bookletIdentity = termsCitation
        ? `${termsCitation.formCode}/${termsCitation.version}/${termsCitation.sha16}#${termsRow?.id ?? ''}`
        : null;

    /**
     * A booklet's acceptance dies with the booklet it was about, IN THE SAME COMMIT.
     *
     * The tick says "I have been shown …", and the note on `bookletAcceptance` above states
     * the rule it exists for: that sentence may only be collected for a display THIS SESSION
     * MADE. The gate is not immune to the page changing under it — `agreement-render` is
     * invalidated on the sign mutation's error path and by the "Reload the document" button,
     * the Terms query resolves after the step is already on screen, and a re-render of the
     * document can cite a different booklet entirely. None of those unmount this component,
     * so without this the ticked state of the OLD booklet would sit there satisfying the gate
     * for the new one — an attestation to a display that never happened, which is the single
     * failure this step exists to end.
     *
     * ── WHY THIS RUNS DURING RENDER AND NOT IN AN EFFECT ──────────────────────
     * An effect is ONE RENDER LATE, and against a CACHED booklet one render is enough to shut
     * the gate for good. `bookletIdentity` carries `termsRow?.id`, so when the new row is
     * already in the react-query cache the identity moves and `bookletPages` resolves in the
     * SAME render — which means `<TermsBooklet key={bookletIdentity}>` REMOUNTS in that
     * commit, seeding its own state from the props it is handed at mount: `tickRef` from
     * `readSeconds`, `reachedRef` from `reachedEnd` (TermsBooklet.tsx:74-75, 103). Handed the
     * OLD booklet's satisfied values it starts already at the seconds floor and skips
     * installing its end-of-text observer — and its observer effect keys on `hasBody` alone,
     * so it never installs later. The effect would then reset the parent to 0 / false and
     * nothing could move it back: `onReadSecondsChange` stops firing at the floor and
     * `onReachedEnd` never fires at all. A signer who can scroll and re-read the booklet all
     * day would never see Continue open.
     *
     * So the reset happens during render, guarded by the identity it last reset for — React's
     * "adjusting state when a prop changes" pattern. React re-runs this component with the new
     * state before it commits anything or mounts a child, so the remounted booklet is handed
     * 0 / false, which is what a booklet nobody has read yet is.
     *
     * `termsRecorded` is cleared with the rest: it is the "already reported" guard, and a new
     * booklet's acceptance is a new thing to report. A render React then discards can at worst
     * re-report one acceptance, which `agreement_record_terms_accepted` answers
     * `already_recorded` without writing a second row.
     */
    const lastBookletIdentity = useRef(bookletIdentity);
    if (lastBookletIdentity.current !== bookletIdentity) {
        lastBookletIdentity.current = bookletIdentity;
        setBookletSeconds(0);
        setBookletEnded(false);
        setBookletAccepted(false);
        termsRecorded.current = null;
    }

    // Terms array follows the derived list; a resumed draft of the wrong length is dropped.
    useEffect(() => {
        setTerms((previous) =>
            previous.length === keyTerms.length ? previous : keyTerms.map(() => false),
        );
    }, [keyTerms]);

    // ── The cross-check on the bytes ──────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;
        if (!rendered?.rendered_md) {
            setHashOk(null);
            return;
        }
        void sha256Hex(rendered.rendered_md).then((digest) => {
            if (cancelled) return;
            // No crypto.subtle (insecure context) means no cross-check, not a blocked signer.
            setHashOk(digest === null ? null : digest === rendered.content_sha256);
        });
        return () => {
            cancelled = true;
        };
    }, [rendered]);

    // ── Draft resilience ──────────────────────────────────────────────────────
    // Also where the OPENING step is decided, so one place answers "which screen first" —
    // a second effect for the no-draft case would race this one on the first commit after
    // the render lands.
    useEffect(() => {
        if (draftRestored.current || !draftScope || !rendered || !agreementDoc) return;
        if (alreadySigned) return;
        draftRestored.current = true;
        const draft = loadDraft(draftScope, rendered.content_sha256);
        if (!draft) {
            // The disclosure on a hire purchase, the intro on everything else.
            setStep(steps[0]);
            return;
        }
        // The drawn signature is never persisted, so a draft that had reached the pad comes
        // back to the pad — empty — rather than to a step that assumes ink exists.
        //
        // VALIDATED AGAINST THIS DOCUMENT'S OWN LIST, not the universe: `booklet` is a real
        // step id that a deposit ceremony does not have, and restoring onto a step this
        // document never renders is a blank screen with a dead Continue.
        const restoredStep = (steps as readonly string[]).includes(draft.step)
            ? (draft.step as Step)
            : steps[0];
        setStep(restoredStep === 'done' ? 'sign' : restoredStep);
        setPage(Math.max(1, draft.page ?? 1));
        setPagesSeen(Math.max(1, draft.pagesSeen ?? 1));
        setReadSeconds(Math.max(0, draft.readSeconds ?? 0));
        setTypedName(draft.typedName ?? '');
        if (Array.isArray(draft.terms)) setTerms(draft.terms);
        // `steps` and `agreementDoc` are read, so they are declared — the `draftRestored` ref
        // is what makes this run once, not a short dependency list.
    }, [draftScope, rendered, agreementDoc, alreadySigned, steps]);

    // A signer who has ALREADY signed opens on the state that tells them so — set once, not
    // forced on every render, because the done step offers a way back into the reader and a
    // per-render override would swallow it.
    const openingStepSet = useRef(false);
    useEffect(() => {
        if (openingStepSet.current || !rendered) return;
        openingStepSet.current = true;
        if (alreadySigned) setStep('done');
    }, [rendered, alreadySigned]);
    useEffect(() => {
        if (!draftScope || !rendered || step === 'done') return;
        saveDraft(draftScope, {
            step,
            page,
            pagesSeen,
            readSeconds,
            terms,
            typedName,
            contentSha256: rendered.content_sha256,
        });
    }, [draftScope, rendered, step, page, pagesSeen, readSeconds, terms, typedName]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [step]);

    const handlePageChange = useCallback((next: number) => {
        setPage(next);
        setPagesSeen((seen) => Math.max(seen, next));
    }, []);

    // ── Recording that the Terms were accepted ────────────────────────────────
    /**
     * `agreement_record_terms_accepted` is fleet-backend's, is being written in this same
     * phase, and EXISTS IN NO ENVIRONMENT YET. So PGRST202 — PostgREST's "no such function" —
     * is swallowed in silence: no toast, no Sentry event, no blocked tick. Any other failure
     * is reported and still lets the signer through.
     *
     * NOTHING HERE IS THE RECORD. The durable evidence is the signature over clause 3.3 of
     * the frozen body, which says in terms that the booklet identified in clause 3.1 was
     * shown and accepted before the Schedule was signed. This call is a convenience for the
     * audit trail, and a convenience must not be able to stop a signature.
     *
     * `agreement_events` IS NOT WRITTEN DIRECTLY and no event_type is invented: that table's
     * policy is `USING (false) WITH CHECK (false)` and only SECURITY DEFINER bodies may
     * insert into it.
     */
    const recordTermsAccepted = useCallback(async () => {
        const row = termsQuery.data;
        if (!agreementDoc || !row || termsRecorded.current === row.id) return;
        // A RETIRED VERSION IS NOT REPORTED, because the RPC refuses anything but a published
        // one. The booklet lookup now admits retired rows on purpose — a signer whose Schedule
        // cites v8 is entitled to v8 after v9 publishes — so this call has a shape it can meet
        // that it could not before, and meeting it would file a Sentry event for a refusal
        // that is correct. Nothing is lost by skipping: the durable evidence is the signature
        // over clause 3.3 of the frozen body, and this call is a convenience for the trail.
        if (row.status !== 'published') return;
        termsRecorded.current = row.id;
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { error } = await (supabase as any).rpc('agreement_record_terms_accepted', {
                p_document_id: agreementDoc.id,
                p_terms_id: row.id,
                p_body_sha256: row.body_sha256,
            });
            if (error) throw error;
        } catch (error) {
            if ((error as { code?: string } | null)?.code === 'PGRST202') return;
            captureError(error, { tags: { area: 'agreement-terms-accepted' } });
        }
    }, [agreementDoc, termsQuery.data]);

    // ── Signing ───────────────────────────────────────────────────────────────
    const signMutation = useMutation({
        mutationFn: async () => {
            if (!agreementDoc || !rendered || !myParty) throw new Error('Document not loaded');

            const dataUrl = padRef.current?.toDataUrl();
            if (!dataUrl) throw new Error('AGREEMENT_SIGNATURE_MISSING: draw your signature');

            // THE SCOPE COMES FROM THE SAME CALL THAT SERVED THE TEXT BEING SIGNED.
            // `agreement_sign` re-derives it through `agreement_path_scope` and refuses a
            // path that does not match, so a scope worked out from `contract_id` here is how
            // an upload lands where SQL will not have it. A null means the document is scoped
            // by neither a contract nor a deposit, which SQL's own CHECK forbids — refusing
            // before the upload costs a message instead of an orphan PNG.
            const path = signatureObjectPath(
                rendered.path_scope,
                agreementDoc.id,
                myParty.party,
                randomUuid(),
            );
            // Named after the SQL error it pre-empts, so Sentry groups the two together. It is
            // not passthrough copy: the signer gets the one calm sentence, as they do for every
            // failure that is not one of the five they can act on.
            if (path === null) {
                throw new Error(
                    'AGREEMENT_SIGNATURE_PATH_INVALID: this document has no storage scope to file a signature under',
                );
            }

            // Uploaded first because `agreement_sign` verifies that the object exists and was
            // put there by THIS session. A signature the RPC then refuses leaves the PNG
            // behind; the bucket's DELETE is admin-only, and a rejected attempt costs one
            // orphan object rather than a signature written against an image nobody uploaded.
            const { error: uploadError } = await supabase.storage
                .from(SIGNATURE_BUCKET)
                .upload(path, dataUrlToBlob(dataUrl), {
                    contentType: 'image/png',
                    upsert: false,
                });
            if (uploadError) throw uploadError;

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { data, error } = await (supabase as any).rpc('agreement_sign', {
                p_document_id: agreementDoc.id,
                p_party: myParty.party,
                // SQL's own hash, verbatim. Never one computed on this page.
                p_content_sha256: rendered.content_sha256,
                p_typed_name: typedName.trim(),
                p_signature_path: path,
                p_consent_text_version: CONSENT_TEXT_VERSION,
                // `p_ip` is left to its NULL default on purpose: a browser cannot know its own
                // address, and a client-supplied one in an audit record is worse than none.
                // The witness path gets a real address because the edge function reads it off
                // the request.
                p_user_agent: navigator.userAgent,
            });
            if (error) throw error;

            return { result: data as { signed?: boolean; reason?: string }, dataUrl };
        },
        onSuccess: ({ result, dataUrl }) => {
            setSignatureProof(dataUrl);
            setStep('done');
            clearDraft(draftScope);

            if (result?.signed === false && result?.reason === 'already_signed') {
                toast.success('You had already signed this document.');
            } else {
                toast.success('Signature recorded');
            }

            void queryClient.invalidateQueries({ queryKey: ['agreement-render', id] });
            void queryClient.invalidateQueries({ queryKey: ['my-pending-signatures'] });
            if (agreementDoc) {
                void queryClient.invalidateQueries({
                    queryKey: ['agreement-documents', agreementDoc.contract_id],
                });
                void queryClient.invalidateQueries({
                    queryKey: ['contract-insights', agreementDoc.contract_id],
                });
            }
        },
        onError: (error) => {
            captureError(error, { tags: { area: 'agreement-sign' } });
            const { code, message } = signingError(error);
            toast.error(message);
            // THE SENTENCE IS THE WHOLE RESPONSE FOR EVERY VERDICT BUT ONE, and
            // `AGREEMENT_PRIOR_SIGNATURE_REQUIRED` is deliberately among them, beside
            // AGREEMENT_SIGN_ORDER and AGREEMENT_CONTRACT_ACTIVE. What follows is not a
            // retry suppressor — it is a rewind, and it is owed only where the BYTES moved
            // under a ceremony whose every gate vouches for them. An advance refused for a
            // signature on another document was read correctly, ticked correctly and drawn
            // correctly; sending that signer back through twelve pages would punish them
            // for the order the instruments are signed in.
            if (code === 'AGREEMENT_CONTENT_CHANGED') {
                // The bytes moved. Drop the draft and send the signer back through the reader.
                clearDraft(draftScope);
                setStep('read');
                setPagesSeen(1);
                setPage(1);
                setReadSeconds(0);
                // AND BACK THROUGH THE BOOKLET. The identity reset above catches a re-render
                // that cites a DIFFERENT booklet; this catches the rest, where the Schedule
                // moved and the citation did not. Every gate on this page vouches for what
                // was shown against the bytes now being signed, and those bytes are being
                // refetched — so the booklet was shown against a document that no longer
                // exists, exactly as the pages and the seconds were.
                setBookletSeconds(0);
                setBookletEnded(false);
                setBookletAccepted(false);
                termsRecorded.current = null;
                void queryClient.invalidateQueries({ queryKey: ['agreement-render', id] });
            }
        },
    });

    // ── Guard rails ───────────────────────────────────────────────────────────
    if (SIGNING_DISABLED) {
        return (
            <Notice
                title="Signing is temporarily unavailable"
                body="Primelane has paused in-app signing. Your documents are safe and nothing has been lost."
                onBack={() => navigate('/documents')}
            />
        );
    }

    if (!id) {
        return (
            <Notice
                title="Document not found"
                body="That signing link does not point at a document on your account."
                onBack={() => navigate('/documents')}
            />
        );
    }

    if (documentQuery.isLoading || renderQuery.isLoading) {
        return (
            <div className="mx-auto w-full max-w-2xl space-y-4 p-4">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-64 w-full" />
                <Skeleton className="h-10 w-full" />
            </div>
        );
    }

    if (documentQuery.error || renderQuery.error || !agreementDoc || !rendered) {
        return (
            <Notice
                title="We could not open this document"
                body="It may have been withdrawn, or it may not belong to your account."
                onBack={() => navigate('/documents')}
            />
        );
    }

    if (documentClosed) {
        return (
            <Notice
                title="This document is no longer valid"
                body="Primelane withdrew it. If a replacement is needed it will appear in your documents."
                onBack={() => navigate('/documents')}
            />
        );
    }

    if (!myParty) {
        return (
            <Notice
                title="Nothing to sign here"
                body="This document does not carry a signature for you."
                onBack={() => navigate('/documents')}
            />
        );
    }

    // FROM THE RENDER, NOT THE ROW. `agreement_documents.title` was dropped as a column —
    // only the pinned template knows it, and `agreement_render` is what reads it off there.
    const title = rendered.title;
    const close = () => navigate('/documents');

    // THE RAIL AND THE FLOW BOTH READ THE DERIVED LIST. A literal index, or a literal "next
    // step", is right for exactly one of the four shapes this ceremony now has.
    const stepIndex = steps.indexOf(step);
    const advance = () => setStep(steps[Math.min(stepIndex + 1, steps.length - 1)]);

    // ── Steps ─────────────────────────────────────────────────────────────────
    if (step === 'disclose') {
        // Both figures are wanted on screen TOGETHER or not at all: the panel says something
        // materially different with a cash price than without one, and a step that stated the
        // second form and then swapped to the first would have disclosed twice, differently.
        const pricesLoading = insightsQuery.isLoading || disclosureQuery.isLoading;
        return (
            <CeremonyShell
                title={title}
                stepIndex={stepIndex}
                steps={steps}
                onClose={close}
                footer={
                    <>
                        <Button className="w-full" disabled={pricesLoading} onClick={advance}>
                            Continue
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                        <CeremonyNote>
                            Stated before the agreement, as the law requires.
                        </CeremonyNote>
                    </>
                }
            >
                {ceremonyBody(
                    pricesLoading ? (
                        <>
                            <Skeleton className="h-6 w-40" />
                            <Skeleton className="h-32 w-full" />
                            <Skeleton className="h-24 w-full" />
                        </>
                    ) : (
                        <DisclosurePanel
                            cashPrice={disclosureQuery.data?.cashPrice ?? null}
                            hirePurchasePrice={insightsQuery.data?.contract?.sold_at ?? null}
                            // THE GRID'S ROW, ON THE GRID'S CONDITION. A contract that has
                            // taken no deposit comes back with zeros and `count: 0`, and the
                            // Schedule's resolver answers NULL there and prints no row — so
                            // the figure is taken through `scheduleDepositCredited`, which is
                            // the same guard the plain-English checklist applies, rather than
                            // off the payload here where a 0 would render "GHS 0.00".
                            depositCredited={scheduleDepositCredited(insightsQuery.data)}
                            // THE SCHEDULE'S OWN WEEKLY INSTALMENT, not `weekly_rate`. This
                            // panel's rows are the money grid's rows under the money grid's
                            // labels, so the figure beside "Weekly Instalment" has to be the
                            // one the grid prints — `contract_instalments.amount`, latest by
                            // `starts_at` (fleet-backend `20260816010000`:309-313). See
                            // `scheduleWeeklyInstalment`, which both agreement-quoting
                            // surfaces derive it through.
                            weeklyInstalment={scheduleWeeklyInstalment(insightsQuery.data)}
                        />
                    ),
                )}
            </CeremonyShell>
        );
    }

    if (step === 'intro') {
        return (
            <CeremonyShell
                title={title}
                stepIndex={stepIndex}
                steps={steps}
                onClose={close}
                footer={
                    <>
                        <Button className="w-full" onClick={() => setStep('read')}>
                            Read the agreement
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                        <CeremonyNote>
                            {pages.length} page{pages.length === 1 ? '' : 's'} · you can continue
                            once you have reached the last page
                        </CeremonyNote>
                    </>
                }
            >
                {ceremonyBody(
                    <>
                        <div>
                            <Eyebrow>Before you sign</Eyebrow>
                            <StepHeading>
                                {keyTerms.length === 1
                                    ? 'What this agreement says'
                                    : `The ${numberWord(keyTerms.length)} things this agreement says`}
                            </StepHeading>
                            <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
                                Plain English first. You will read the full document next.
                            </p>
                        </div>

                        <div className="flex flex-col">
                            {keyTerms.map((term, index) => (
                                <KeyTermRow
                                    key={term.id}
                                    index={index + 1}
                                    summary={term.summary}
                                    detail={term.detail}
                                />
                            ))}
                        </div>

                        <FrozenNote>
                            {agreementDoc.reference_no}
                            {rendered.frozen && rendered.frozen_at
                                ? ` · locked ${format(new Date(rendered.frozen_at), 'MMM d, yyyy')}`
                                : ''}
                        </FrozenNote>

                        <SignatoryChips signatures={rendered.parties} />
                    </>,
                )}
            </CeremonyShell>
        );
    }

    if (step === 'read') {
        const gateOpen = readerGateOpen(pagesSeen, pages.length, readSeconds, READ_SECONDS_FLOOR);
        return (
            <CeremonyShell
                title={title}
                stepIndex={stepIndex}
                steps={steps}
                onClose={close}
                footer={
                    <>
                        <Button className="w-full" disabled={!gateOpen} onClick={advance}>
                            Continue
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                        <CeremonyNote>
                            Take your time — this is the document you are signing.
                        </CeremonyNote>
                    </>
                }
            >
                <DocumentReader
                    pages={pages}
                    page={page}
                    onPageChange={handlePageChange}
                    pagesSeen={pagesSeen}
                    readSeconds={readSeconds}
                    onReadSecondsChange={setReadSeconds}
                    secondsFloor={READ_SECONDS_FLOOR}
                    frozenNotice={
                        rendered.frozen
                            ? 'Locked — this is exactly the text every party signs.'
                            : null
                    }
                />
            </CeremonyShell>
        );
    }

    if (step === 'booklet' && termsCitation) {
        const hasBooklet = (bookletPages?.length ?? 0) > 0;
        const bookletGateOpen = bookletEnded && bookletSeconds >= READ_SECONDS_FLOOR;
        // NOTHING TO SHOW MEANS NOTHING TO GATE. An unresolved booklet leaves the signer with
        // the citation, which is real information they are entitled to, and a Continue that
        // works — a floor counted against an empty screen would be pure theatre.
        const canContinue = !hasBooklet ? !termsQuery.isLoading : bookletAccepted;
        return (
            <CeremonyShell
                title={title}
                stepIndex={stepIndex}
                steps={steps}
                onClose={close}
                footer={
                    <>
                        <Button className="w-full" disabled={!canContinue} onClick={advance}>
                            Continue
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                        <CeremonyNote>
                            {!hasBooklet
                                ? 'These Terms are identified inside the document you are signing.'
                                : !bookletGateOpen
                                    ? 'Read to the end of the Terms to accept them.'
                                    : bookletAccepted
                                        ? 'Accepted.'
                                        : 'Accept the Terms to continue.'}
                        </CeremonyNote>
                    </>
                }
            >
                {ceremonyBody(
                    <>
                        {/*
                          KEYED ON THE BOOKLET, SO THE RESET ABOVE IS A REAL ONE. This child
                          seeds its own second counter from `readSeconds` at mount and never
                          resyncs it, and it DISCONNECTS its end-of-text observer once it has
                          fired. Clearing the parent's four states alone would therefore leave
                          a stale count pushing itself back up a second later and an end gate
                          that can never fire again — a booklet the signer cannot get past.
                          Remounting restarts both, which is what "shown again" means.
                        */}
                        <TermsBooklet
                            key={bookletIdentity ?? 'booklet'}
                            title={bookletName}
                            citationLabel={termsCitationLabel(termsCitation)}
                            partyNoun={PARTY_NOUNS[agreementDoc.doc_type] ?? 'Driver'}
                            pages={bookletPages}
                            loading={termsQuery.isLoading}
                            readSeconds={bookletSeconds}
                            onReadSecondsChange={setBookletSeconds}
                            secondsFloor={READ_SECONDS_FLOOR}
                            reachedEnd={bookletEnded}
                            onReachedEnd={() => setBookletEnded(true)}
                            pdfUrl={termsBookletUrl(termsRow?.storage_path)}
                        />

                        {hasBooklet && (
                            <ConfirmRow
                                checked={bookletAccepted}
                                disabled={!bookletGateOpen}
                                onCheckedChange={(checked) => {
                                    setBookletAccepted(checked);
                                    if (checked) void recordTermsAccepted();
                                }}
                                title={bookletAcceptance}
                                detail={termsCitationLabel(termsCitation)}
                            />
                        )}
                    </>,
                )}
            </CeremonyShell>
        );
    }

    if (step === 'terms') {
        const allConfirmed = terms.length > 0 && terms.every(Boolean);
        return (
            <CeremonyShell
                title={title}
                stepIndex={stepIndex}
                steps={steps}
                onClose={close}
                footer={
                    <>
                        <Button
                            className="w-full"
                            disabled={!allConfirmed}
                            onClick={() => setStep('sign')}
                        >
                            Continue to signing
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                        <CeremonyNote>
                            {allConfirmed
                                ? 'All confirmed.'
                                : 'Confirm every statement to continue.'}
                        </CeremonyNote>
                    </>
                }
            >
                {ceremonyBody(
                    <>
                        <div>
                            <Eyebrow>Confirm the key terms</Eyebrow>
                            <StepHeading>Tick each one to confirm it is correct</StepHeading>
                        </div>

                        <div>
                            {keyTerms.map((term, index) => (
                                <ConfirmRow
                                    key={term.id}
                                    checked={terms[index] === true}
                                    onCheckedChange={(checked) =>
                                        setTerms((previous) => {
                                            const next = [...previous];
                                            next[index] = checked;
                                            return next;
                                        })
                                    }
                                    title={term.confirmation}
                                    detail={term.title}
                                />
                            ))}
                        </div>

                        <p className="text-[11.5px] leading-relaxed text-muted-foreground">
                            If any of these is wrong, stop and contact Primelane before signing.
                        </p>
                    </>,
                )}
            </CeremonyShell>
        );
    }

    if (step === 'sign') {
        const nameReady = typedName.trim().length >= 2;
        const canSubmit =
            nameReady && attested && hasInk && hashOk !== false && !signMutation.isPending;

        return (
            <CeremonyShell
                title={title}
                stepIndex={stepIndex}
                steps={steps}
                onClose={close}
                footer={
                    <>
                        <Button
                            className="w-full"
                            disabled={!canSubmit}
                            onClick={() => signMutation.mutate()}
                        >
                            {signMutation.isPending && (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            )}
                            Submit signature
                        </Button>
                        <CeremonyNote>
                            Your signature is stored with a time stamp and the version of the
                            document you read.
                        </CeremonyNote>
                    </>
                }
            >
                {ceremonyBody(
                    <>
                        <div>
                            <Eyebrow>Your signature</Eyebrow>
                            <StepHeading>Sign with your finger</StepHeading>
                        </div>

                        {hashOk === false && (
                            <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-3.5 text-[12.5px] text-destructive">
                                <p className="font-semibold">This document has changed</p>
                                <p className="mt-1">
                                    The text on screen no longer matches what Primelane has on
                                    record, so it cannot be signed. Reload and read it again.
                                </p>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="mt-3"
                                    onClick={() => {
                                        clearDraft(draftScope);
                                        void queryClient.invalidateQueries({
                                            queryKey: ['agreement-render', id],
                                        });
                                        setStep('read');
                                        setPage(1);
                                        setPagesSeen(1);
                                        setReadSeconds(0);
                                        // AND BACK THROUGH THE BOOKLET, for the reason the
                                        // sign mutation's AGREEMENT_CONTENT_CHANGED path
                                        // resets it: the bytes this session's gates vouch for
                                        // are the ones being refetched here, so the booklet
                                        // was shown against a document that no longer exists,
                                        // exactly as the pages and the seconds were. The
                                        // identity reset above catches a reload that cites a
                                        // DIFFERENT booklet; a Schedule that moved while its
                                        // citation did not is the case it cannot see.
                                        setBookletSeconds(0);
                                        setBookletEnded(false);
                                        setBookletAccepted(false);
                                        termsRecorded.current = null;
                                    }}
                                >
                                    <RotateCw className="h-4 w-4" />
                                    Reload the document
                                </Button>
                            </div>
                        )}

                        <SigningAs
                            name={driverName}
                            sub={insightsQuery.data?.driver?.phone ?? undefined}
                        />

                        <div className="space-y-1.5">
                            <Label htmlFor="typed-name" className="text-[12.5px]">
                                Type your full name
                            </Label>
                            <Input
                                id="typed-name"
                                value={typedName}
                                autoComplete="name"
                                placeholder={driverName}
                                onChange={(event) => setTypedName(event.target.value)}
                            />
                            <p className="text-[11px] text-muted-foreground">
                                Typed alongside the drawn signature, as the agreement requires.
                            </p>
                        </div>

                        <SignaturePad
                            ref={padRef}
                            onInkChange={setHasInk}
                            caption={format(new Date(), 'MMM d, yyyy')}
                            disabled={signMutation.isPending}
                        />

                        <ConfirmRow
                            checked={attested}
                            onCheckedChange={setAttested}
                            title={ATTESTATION}
                            detail={`Consent version ${CONSENT_TEXT_VERSION}`}
                        />
                    </>,
                )}
            </CeremonyShell>
        );
    }

    // ── done ──────────────────────────────────────────────────────────────────
    const executed = rendered.status === 'COMPLETED';
    const mySignedAt = myParty.signed_at ?? null;
    const trail = signDoneTrail({
        docType: agreementDoc.doc_type,
        documentStatus: rendered.status,
        mySignedAt,
        parties: rendered.parties,
        finalizedAt: agreementDoc.finalized_at,
        contractDocuments: contractDocumentsQuery.data,
    });

    return (
        <CeremonyShell
            title={title}
            stepIndex={stepIndex}
            steps={steps}
            onClose={close}
            footer={
                <>
                    {/* THE COPY IS READ, NOT SAVED (owner ruling, 2026-08-21). This was
                        "Download your copy" and it wrote a PDF to the device; it now opens the
                        at-rest reader, which is the same route the documents card takes. The
                        button still appears only once the instrument has actually been sealed
                        — before that there is no executed copy to open, and the link above
                        goes back through the text. */}
                    <Button
                        variant="outline"
                        className="w-full"
                        disabled={!agreementDoc.finalized_at}
                        onClick={() => navigate(`/agreement/${agreementDoc.id}`)}
                    >
                        <Eye className="h-4 w-4" />
                        View your copy
                    </Button>
                    <Button className="w-full" onClick={close}>
                        Back to documents
                    </Button>
                </>
            }
        >
            {ceremonyBody(
                <>
                    <div className="pt-6 text-center">
                        <DoneMark />
                        <h2 className="mt-4 text-xl font-bold tracking-tight">
                            {executed ? 'Agreement complete' : 'Signature recorded'}
                        </h2>
                        <p className="mx-auto mt-2 max-w-sm text-[13px] leading-relaxed text-muted-foreground">
                            {mySignedAt
                                ? `Signed ${format(new Date(mySignedAt), 'MMM d, yyyy')}. `
                                : ''}
                            {trail.next}
                        </p>
                        {/* Signed documents stay readable: the executed PDF only exists after
                            the last signature, and until then this is the only way back to the
                            text. */}
                        <Button
                            variant="link"
                            className="mt-1 h-auto p-0 text-[12.5px]"
                            onClick={() => {
                                setPage(1);
                                setStep('read');
                            }}
                        >
                            Read the document again
                        </Button>
                    </div>

                    {signatureProof && (
                        <div className="rounded-xl border border-border bg-white p-3">
                            <img
                                src={signatureProof}
                                alt="Your signature"
                                className="mx-auto h-20 object-contain"
                            />
                            <p className="mt-2 text-center text-[11px] text-muted-foreground">
                                {typedName || driverName} · {agreementDoc.reference_no}
                            </p>
                        </div>
                    )}

                    <div className="rounded-xl border border-border p-4">
                        <Eyebrow>What happens next</Eyebrow>
                        <div className="mt-1">
                            {/* `row`, not `step`: this component already has a `step` and a
                                trail row is not one of the five. */}
                            {trail.steps.map((row) => (
                                <TrailRow
                                    key={row.id}
                                    state={row.state}
                                    title={row.title}
                                    detail={row.detail}
                                />
                            ))}
                        </div>
                    </div>

                    <SignatoryChips signatures={rendered.parties} />
                </>,
            )}
        </CeremonyShell>
    );
}

// ─── Small helpers ───────────────────────────────────────────────────────────

function numberWord(count: number): string {
    const words = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven'];
    return words[count] ?? String(count);
}

function Notice({
    title,
    body,
    onBack,
}: {
    title: string;
    body: string;
    onBack: () => void;
}) {
    return (
        <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 bg-background px-6 text-center">
            <FileText className="h-10 w-10 text-muted-foreground" />
            <div>
                <h1 className="text-lg font-semibold">{title}</h1>
                <p className="mx-auto mt-1.5 max-w-sm text-sm text-muted-foreground">{body}</p>
            </div>
            <Button variant="outline" onClick={onBack}>
                Back to documents
            </Button>
        </div>
    );
}
